import { DocumentNode, useMutation } from '@apollo/client';
import { FORM_ERROR } from 'final-form';

import { gql } from '../../../../__generated__';
import { AddSessionNoteMutation, AddSessionNoteMutationVariables, AddStudentNoteMutation, AddStudentNoteMutationVariables, NoteOriginType, NoteTypeEnum } from '../../../../__generated__/graphql';
import { ApiValidationError, FormSubmitCallback, mapApiFieldValidationErrors } from '../../../../components/Form';
import { ApiUnknownError } from '../../../../components/Form/mapApiFieldValidationErrors';
import { useWorkListContext } from '../../../../context/WorkListsContext';
import { useSlideInContext } from "../../../../context/SlideInContext";
import NoteForm from './NoteForm';
import { SESSION_NOTES_QUERY } from '../../../DashboardPages/components/HistoryTable/HistoryTable';
import { NoteFormProps } from './NoteForm';
import { useStudentContext } from '../../../../context/studentContext';
import { STUDENT_NOTES_QUERY } from '../../../StudentPages/components/StudentNoteTable';
import { useParams } from 'react-router';
import { useEffect } from 'react';
export const ADD_SESSION_NOTE_MUTATION = gql(/* GraphQL */`
    mutation AddSessionNote($sessionId: UUID! $type: NoteTypeEnum!, $body: String!, $origin: NoteOriginType!) {
        addSessionNote(input: { sessionId: $sessionId, type: $type, body: $body, origin:$origin }) {
            __typename
            errors {
                __typename
                ... on UnknownError {
                    message
                }
                ... on ValidationError {
                    message
                    errors {
                        fieldName
                        messages
                    }
                }
            }
        }
    }
`);
export const ADD_STUDENT_NOTE_MUTATION = gql(`
    mutation AddStudentNote($studentId: UUID! $type: NoteTypeEnum!, $body: String!, $origin: NoteOriginType!) {
        addStudentNote(input: { studentId: $studentId, type: $type, body: $body, origin:$origin }) {
            __typename
            errors {
                __typename
                ... on UnknownError {
                    message
                }
                ... on ValidationError {
                    message
                    errors {
                        fieldName
                        messages
                    }
                }
            }
        }
    }`)
export const formatBody = (value: string) => {
    return value
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/\b\w/g, (match) => match.toUpperCase()) // Capitalize first letter of each word
        .replace(/\B\w/g, (match) => match.toLowerCase()); // Lowercase the remaining characters
}
export default function AddNote() {
    const [addSessionNoteMutation] = useMutation<AddSessionNoteMutation, AddSessionNoteMutationVariables>(ADD_SESSION_NOTE_MUTATION);
    const [addStudentNoteMutation] = useMutation<AddStudentNoteMutation, AddStudentNoteMutationVariables>(ADD_STUDENT_NOTE_MUTATION);
    const { toggleDrawer } = useSlideInContext();
    const { session } = useWorkListContext();
    const { studentData } = useStudentContext();
    const params = useParams()
    const isSessionSpecific = (window.location.pathname.includes("notes") && !window.location.pathname.includes("children")) || (window.location.pathname.includes("session"))
    const noteType = isSessionSpecific ? NoteTypeEnum.SessionNote : NoteTypeEnum.General
    const noteOrigin = isSessionSpecific ? window.location.pathname.includes("notes") ? NoteOriginType.HistoryTable : NoteOriginType.ResultsTable : NoteOriginType.NotesTable
    const queriesToRefetch: { query: DocumentNode; variable?: any }[] = [
        session?.id ? {
            query: SESSION_NOTES_QUERY,
            variables: {
                sessionId: session?.id
            }
        } : null,
        studentData?.id ? {
            query: STUDENT_NOTES_QUERY,
            variables: {
                studentId: studentData?.id
            }
        } : null].filter(Boolean) as { query: DocumentNode; variables: any }[];

    const handleAddNote: FormSubmitCallback<NoteFormProps> = async (values): Promise<ReturnType<FormSubmitCallback>> => {
        if (isSessionSpecific) {
            const response = await addSessionNoteMutation({
                variables: {
                    sessionId: session?.id,
                    type: noteType,
                    origin: noteOrigin,
                    body: values.note
                },
                refetchQueries: queriesToRefetch
            })

            if (response.data?.addSessionNote.errors) {
                const validationError = response.data.addSessionNote.errors
                    .find(e => e.__typename === "ValidationError") as ApiValidationError;

                if (validationError.errors) {
                    const fieldErrors = validationError
                        ? mapApiFieldValidationErrors(validationError)
                        : null;

                    return fieldErrors;
                }

                const unknownError = response.data.addSessionNote.errors
                    .find(e => e.__typename === 'UnknownError') as ApiUnknownError;

                if (unknownError) {
                    return { [FORM_ERROR]: unknownError.message }
                }
            }
            toggleDrawer();
            return undefined
        } else {
            const response = await addStudentNoteMutation({
                variables: {
                    studentId: studentData?.id ?? params.sid,
                    type: noteType,
                    origin: noteOrigin,
                    body: values.note
                },
                refetchQueries: [{
                    query: STUDENT_NOTES_QUERY,
                    variables: {
                        studentId: studentData?.id
                    }
                }]
            })

            if (response.data?.addStudentNote.errors) {
                const validationError = response.data.addStudentNote.errors
                    .find(e => e.__typename === "ValidationError") as ApiValidationError;

                if (validationError.errors) {
                    const fieldErrors = validationError
                        ? mapApiFieldValidationErrors(validationError)
                        : null;

                    return fieldErrors;
                }

                const unknownError = response.data.addStudentNote.errors
                    .find(e => e.__typename === 'UnknownError') as ApiUnknownError;

                if (unknownError) {
                    return { [FORM_ERROR]: unknownError.message }
                }
            }
            toggleDrawer();
            return undefined

        }


    }

    return (
        <div>
            <NoteForm
                onCancel={() => toggleDrawer()}
                onSubmit={handleAddNote}
            />
        </div>
    )
}
